import React, { useEffect, useLayoutEffect, useState } from 'react';
import { ComponentProps, PageProps } from './index.types';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { SkeletonPropertyCardLoaderComponent } from '../../../../molecules/skeletonPropertyCardLoader';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { hqvInitialState } from '../../../../reducers/HQV';
import { StyledFormContainer } from './SearchForm.styles';
import SearchFormComponent from './SearchFormComponent';
import { useHQVStore } from '../../../../modules/store/hqvZustandStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';

const SearchForm: React.FC<ComponentProps> = pageProps => {
  const [loadForm, setLoadForm] = useState(false);

  const envVarsObject = useClientEnvVarsStore(state => state.envVarsObject);

  const [searchLoadForms, setSearchLoadForms] = useState(false);
  const [searchFormScrolled, setSearchFormScrolled] = useState(false);

  /**
   * Initialize HQV Zustand store when EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND is enabled and we're
   * rendering the ADF SearchForm. SERP_ZUSTAND is intentionally NOT consulted here so a SERP
   * toggle cannot flip ADF into Zustand mode (and vice versa). This mirrors the flag isolation
   * enforced by useConditionalHQVContextADF used by SearchFormComponent.
   *
   * Guard against clobbering an already-initialized store: if HQVWrapper is also authored on the
   * ADF page and ran its own `hqvInitialization` (which is followed by `setEnableUseSWR(true)`),
   * we must not re-seed with `hqvInitialState` here or we'd overwrite `enableUseSWR` back to false
   * and force SearchFormComponent's isAdfLegacyMode gate to true, producing a double-modal render.
   * This mirrors the same guard used in SearchResultsMainComponent for SERP.
   */
  useLayoutEffect(() => {
    const featureFlags = envVarsObject?.['featureFlags'] as unknown as Record<string, FLAG_TYPE> | undefined;
    const shouldUseZustand = featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false;
    const enableAdfSearchForm = (pageProps as unknown as PageProps)?.enableAdfSearchForm;

    if (shouldUseZustand && enableAdfSearchForm) {
      const currentState = useHQVStore.getState().hqvState;
      const isDefaultState =
        !currentState.modalOpenState && !currentState.propertyIDvalue && !currentState.enableUseSWR;
      if (isDefaultState) {
        useHQVStore.getState().hqvInitialization(hqvInitialState);
      }
    }
  }, [(pageProps as unknown as PageProps)?.enableAdfSearchForm, envVarsObject]);
  useEffect(() => {
    eventUtil.on('loadSearchForm', res => {
      setLoadForm(res);
    });
    document.addEventListener('scroll', () => {
      setSearchFormScrolled(true);
    });
    return () => {
      document.removeEventListener('scroll', () => {
        setSearchFormScrolled(false);
      });
    };
  }, []);

  const setSearchLoadForm = () => {
    setSearchLoadForms(true);
  };

  return (
    <StyledFormContainer
      className={
        (pageProps as unknown as PageProps)?.enableAdfSearchForm ||
        (pageProps as unknown as PageProps)?.cqType?.includes('mi-aem-shop-spa')
          ? searchFormScrolled
            ? 'serp-search-form-scrolled'
            : 'serp-search-form'
          : 'serp-search-form alternate'
      }
    >
      {!searchLoadForms && <SkeletonPropertyCardLoaderComponent isViewportL={true} isSearchForm={true} />}
      {loadForm && (
        <SearchFormComponent pageProps={pageProps as unknown as PageProps} setSearchLoadForm={setSearchLoadForm} />
      )}
    </StyledFormContainer>
  );
};

export default SearchForm;
//Config for aem editable components
export const SearchFormConfig = {
  emptyLabel: 'SearchForm',
  isEmpty: false,
  //tobe changed
  resourceType: `mi-aem-shop-spa/components/content/searchform/v1/searchform`,
};
//Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchFormComponentEditable = (props: any) =>
  props.cqPath.includes('experiencefragment') ? (
    <SearchForm {...props} />
  ) : (
    <EditableComponent config={SearchFormConfig} {...props}>
      <SearchForm {...props} />
    </EditableComponent>
  );
